/*
 * Cart drawer: complementary products ("Supplerende produkter").
 * Fetches sections/cart-complementary.liquid through the Product Recommendations API for the
 * products in the cart, merges the results and shows them as a horizontal swipe list.
 */
if ( ! customElements.get('cart-complementary') ) {

	class CartComplementary extends HTMLElement {

		connectedCallback(){

			this.list = this.querySelector('[data-cart-comp-list]');
			this.cartForm = document.getElementById('AjaxCartForm');
			if ( ! this.list || ! this.cartForm ) return;

			this.limit = parseInt(this.dataset.limit) || 6;
			this.maxSources = 3; // how many cart products we ask recommendations for
			this.currentKey = null;
			this.dirty = true;
			this.timer = null;

			// the theme replaces the content of #AjaxCartForm on every cart change
			new MutationObserver(() => {
				this.dirty = true;
				this.schedule();
			}).observe(this.cartForm, { childList: true });

			// only load when the drawer is actually opened
			if ( 'IntersectionObserver' in window ) {
				new IntersectionObserver(entries => {
					if ( entries.some(entry => entry.isIntersecting) ) this.schedule();
				}).observe(this.closest('.sidebar__body') || this);
			}

			this.addEventListener('submit', e => {
				const form = e.target.closest('[data-cart-comp-form]');
				if ( form ) {
					e.preventDefault();
					this.addToCart(form);
				}
			});

			this.schedule();

		}

		isVisible(){
			return this.cartForm.offsetParent !== null;
		}

		schedule(){
			clearTimeout(this.timer);
			this.timer = setTimeout(() => {
				if ( this.dirty && this.isVisible() ) this.load();
			}, 150);
		}

		cartProductIds(){
			const ids = [];
			this.cartForm.querySelectorAll('[data-js-cart-item]').forEach(item => {
				const id = item.dataset.productId;
				if ( id && ! ids.includes(id) ) ids.push(id);
			});
			return ids;
		}

		fetchItems(productId, intent){
			const url = `${this.dataset.url}?section_id=${this.dataset.section}&product_id=${productId}&limit=${this.limit}&intent=${intent}`;
			return fetch(url)
				.then(response => response.ok ? response.text() : '')
				.then(text => Array.from(
					new DOMParser().parseFromString(text, 'text/html').querySelectorAll('.cart-comp__item')
				))
				.catch(() => []);
		}

		async load(){

			const inCart = this.cartProductIds();
			const key = inCart.join(',');
			this.dirty = false;
			if ( key === this.currentKey ) return;
			this.currentKey = key;

			if ( inCart.length === 0 ) {
				this.render([]);
				return;
			}

			const sources = inCart.slice(0, this.maxSources);
			let results = await Promise.all(sources.map(id => this.fetchItems(id, 'complementary')));
			let items = this.merge(results, inCart);

			if ( items.length === 0 && this.dataset.fallback === 'true' ) {
				results = await Promise.all(sources.map(id => this.fetchItems(id, 'related')));
				items = this.merge(results, inCart);
			}

			// the cart changed again while we were loading
			if ( key !== this.cartProductIds().join(',') ) {
				this.currentKey = null;
				this.dirty = true;
				this.schedule();
				return;
			}

			this.render(items);

		}

		// round robin across the cart products, without duplicates and without products already in the cart
		merge(results, inCart){
			const items = [];
			const seen = new Set(inCart);
			const longest = Math.max(0, ...results.map(list => list.length));
			for ( let i = 0; i < longest && items.length < this.limit; i++ ) {
				for ( const list of results ) {
					const item = list[i];
					if ( item && ! seen.has(item.dataset.productId) && items.length < this.limit ) {
						seen.add(item.dataset.productId);
						items.push(item);
					}
				}
			}
			return items;
		}

		render(items){
			this.list.replaceChildren(...items);
			this.list.scrollLeft = 0;
			this.hidden = items.length === 0;
		}

		addToCart(form){

			const item = form.closest('.cart-comp__item');
			const button = form.querySelector('button[type="submit"]');
			const error = item.querySelector('[data-cart-comp-error]');
			const variantId = form.querySelector('[name="id"]').value;

			error.textContent = '';
			button.disabled = true;
			item.classList.add('cart-comp__item--working');

			fetch(`${KROWN.settings.routes.cart_add_url}.js`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
				body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
			})
				.then(response => response.json().then(data => ({ ok: response.ok, data })))
				.then(({ ok, data }) => {
					if ( ! ok ) {
						error.textContent = typeof data.description === 'string' ? data.description : ( data.message || '' );
						return;
					}
					if ( typeof window.refreshCart === 'function' ) window.refreshCart();
					if ( window.KROWN && KROWN.functions && KROWN.functions.eventDispatcher ) {
						KROWN.functions.eventDispatcher('krown:cart:add', { productForm: form, variantId });
					}
				})
				.catch(e => console.log(e))
				.finally(() => {
					button.disabled = false;
					item.classList.remove('cart-comp__item--working');
				});

		}

	}

	customElements.define('cart-complementary', CartComplementary);

}
